import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cajasAPI, cuentasBancariasAPI, clientesAPI, bancosAPI, puestaEnMarchaAPI, sistemaAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { toast } from 'react-toastify';
import '../Clientes.css';

const PuestaEnMarcha = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { fechaHoy } = useZonaHoraria();
  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCajaId, setSavingCajaId] = useState(null);
  const [savingCuentaId, setSavingCuentaId] = useState(null);
  const [savingCheque, setSavingCheque] = useState(false);
  const [estadoMigraciones, setEstadoMigraciones] = useState(null);
  const [loadingMigraciones, setLoadingMigraciones] = useState(false);
  const [reEjecutandoMigraciones, setReEjecutandoMigraciones] = useState(false);
  const [chequeForm, setChequeForm] = useState({
    cliente_id: '',
    banco_id: '',
    numero: '',
    fecha_emision: '',
    fecha_cobro: '',
    importe: '',
    es_electronico: false,
    fecha: fechaHoy(),
    concepto: 'Cheque en cartera pre-sistema',
  });

  const puedeAcceder = hasPermission('admin_puesta_en_marcha') || hasPermission('gestionar_usuarios');
  const puedeMigraciones = hasPermission('gestionar_usuarios');

  const loadEstadoMigraciones = useCallback(async () => {
    if (!puedeMigraciones) return;
    setLoadingMigraciones(true);
    try {
      const res = await sistemaAPI.getEstadoMigraciones();
      if (res.success) setEstadoMigraciones(res.data);
    } catch (e) {
      toast.error(e.message || 'Error al cargar estado de migraciones');
    } finally {
      setLoadingMigraciones(false);
    }
  }, [puedeMigraciones]);

  const reEjecutarMigraciones = async () => {
    if (!window.confirm('¿Re-ejecutar migraciones y verificar el esquema?')) return;
    setReEjecutandoMigraciones(true);
    try {
      const res = await sistemaAPI.reEjecutarMigraciones();
      if (res.success) {
        toast.success(res.message || 'Migraciones verificadas');
        await loadEstadoMigraciones();
      } else {
        toast.error(res.message || 'Error al re-ejecutar migraciones');
      }
    } catch (e) {
      toast.error(e.message || 'Error al re-ejecutar migraciones');
    } finally {
      setReEjecutandoMigraciones(false);
    }
  };

  const loadData = useCallback(async () => {
    if (!hasPermission('admin_puesta_en_marcha')) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [resCajas, resCuentas, resClientes, resBancos] = await Promise.all([
        cajasAPI.getAll(),
        cuentasBancariasAPI.getAll(),
        clientesAPI.getAll({ activo: true }),
        bancosAPI.getAll(),
      ]);
      if (resCajas.success) setCajas(resCajas.data || []);
      if (resCuentas.success) setCuentas(resCuentas.data || []);
      if (resClientes.success) setClientes(resClientes.data || []);
      if (resBancos.success) setBancos(resBancos.data || []);
    } catch (e) {
      toast.error('Error al cargar datos de puesta en marcha');
    } finally {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    if (!puedeAcceder) {
      navigate('/dashboard');
      return;
    }
    loadData();
    loadEstadoMigraciones();
  }, [puedeAcceder, navigate, loadData, loadEstadoMigraciones]);

  const actualizarSaldoCaja = async (caja) => {
    setSavingCajaId(caja.id);
    try {
      const res = await puestaEnMarchaAPI.updateSaldoCaja(caja.id, parseFloat(caja.saldo_inicial) || 0);
      if (res.success) toast.success(`Saldo inicial de ${caja.nombre} guardado`);
      else toast.error(res.message || 'Error al guardar');
    } catch (e) {
      toast.error(e.message || 'Error al guardar caja');
    } finally {
      setSavingCajaId(null);
    }
  };

  const actualizarSaldoCuenta = async (cuenta) => {
    setSavingCuentaId(cuenta.id);
    try {
      const res = await puestaEnMarchaAPI.updateSaldoCuenta(cuenta.id, parseFloat(cuenta.saldo_inicial) || 0);
      if (res.success) toast.success(`Saldo inicial de cuenta ${cuenta.numero_cuenta} guardado`);
      else toast.error(res.message || 'Error al guardar');
    } catch (e) {
      toast.error(e.message || 'Error al guardar cuenta');
    } finally {
      setSavingCuentaId(null);
    }
  };

  const registrarCheque = async (e) => {
    e.preventDefault();
    if (!chequeForm.cliente_id || !chequeForm.banco_id || !chequeForm.numero || !chequeForm.fecha_emision || !chequeForm.importe) {
      toast.error('Complete cliente, banco, número, fecha de emisión e importe');
      return;
    }
    setSavingCheque(true);
    try {
      const res = await puestaEnMarchaAPI.registrarChequeCartera({
        cliente_id: Number(chequeForm.cliente_id),
        banco_id: Number(chequeForm.banco_id),
        numero: chequeForm.numero.trim(),
        fecha_emision: chequeForm.fecha_emision,
        fecha_cobro: chequeForm.fecha_cobro || null,
        importe: parseFloat(chequeForm.importe),
        es_electronico: chequeForm.es_electronico,
        fecha: chequeForm.fecha || fechaHoy(),
        concepto: chequeForm.concepto.trim() || 'Cheque en cartera pre-sistema',
      });
      if (res.success) {
        toast.success(`Cheque registrado (recibo N° ${res.data?.numero_recibo})`);
        setChequeForm((prev) => ({
          ...prev,
          numero: '',
          importe: '',
          fecha_cobro: '',
        }));
      }
    } catch (err) {
      toast.error(err.message || 'Error al registrar cheque');
    } finally {
      setSavingCheque(false);
    }
  };

  if (!puedeAcceder) return null;
  if (loading && hasPermission('admin_puesta_en_marcha')) return <div className="loading">Cargando...</div>;

  return (
    <div className="clientes">
      <div className="page-header">
        <h1>Puesta en marcha</h1>
        <p style={{ color: '#7f8c8d', marginTop: 4 }}>
          Configuración inicial de saldos y cheques existentes antes del go-live.
        </p>
      </div>

      {puedeMigraciones && (
        <section style={{ marginBottom: 32, padding: 16, border: '1px solid #dfe6e9', borderRadius: 8, background: '#fafbfc' }}>
          <h2>Estado de migraciones / versión</h2>
          {loadingMigraciones ? (
            <p>Cargando...</p>
          ) : estadoMigraciones ? (
            <>
              <p><strong>Versión desplegada:</strong> {estadoMigraciones.version_desplegada || '—'}</p>
              <p><strong>Versión en BD:</strong> {estadoMigraciones.version_bd || '(sin registrar)'}</p>
              <p><strong>Última verificación:</strong> {estadoMigraciones.version_verificada_at || '—'}</p>
              {estadoMigraciones.chequeo && !estadoMigraciones.chequeo.ok && (
                <div style={{ color: '#c0392b', marginTop: 8 }}>
                  <p><strong>Esquema incompleto.</strong></p>
                  {estadoMigraciones.chequeo.faltantes?.length > 0 && (
                    <ul>
                      {estadoMigraciones.chequeo.faltantes.map((f, i) => (
                        <li key={i}>
                          {f.tipo === 'columna' ? `${f.tabla}.${f.columna}` : `tabla ${f.tabla}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <button
                type="button"
                className="btn-primary"
                style={{ marginTop: 12 }}
                onClick={reEjecutarMigraciones}
                disabled={reEjecutandoMigraciones}
              >
                {reEjecutandoMigraciones ? 'Ejecutando...' : 'Re-ejecutar migraciones'}
              </button>
            </>
          ) : (
            <p>No hay datos de migraciones.</p>
          )}
        </section>
      )}

      {hasPermission('admin_puesta_en_marcha') && (
      <>
      <section style={{ marginBottom: 32 }}>
        <h2>Saldo inicial de cajas</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Caja</th>
                <th>Moneda</th>
                <th>Saldo inicial</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {cajas.map((caja) => (
                <tr key={caja.id}>
                  <td>{caja.nombre}</td>
                  <td>{caja.moneda || 'ARS'}</td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={caja.saldo_inicial ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCajas((prev) =>
                          prev.map((c) => (c.id === caja.id ? { ...c, saldo_inicial: val } : c))
                        );
                      }}
                      style={{ width: 140 }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={savingCajaId === caja.id}
                      onClick={() => actualizarSaldoCaja(caja)}
                    >
                      {savingCajaId === caja.id ? 'Guardando...' : 'Guardar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2>Saldo inicial de cuentas bancarias</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Banco</th>
                <th>N° cuenta</th>
                <th>Tipo</th>
                <th>Saldo inicial</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {cuentas.map((cuenta) => (
                <tr key={cuenta.id}>
                  <td>{cuenta.banco_nombre}</td>
                  <td>{cuenta.numero_cuenta}</td>
                  <td>{cuenta.tipo}</td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={cuenta.saldo_inicial ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCuentas((prev) =>
                          prev.map((c) => (c.id === cuenta.id ? { ...c, saldo_inicial: val } : c))
                        );
                      }}
                      style={{ width: 140 }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={savingCuentaId === cuenta.id}
                      onClick={() => actualizarSaldoCuenta(cuenta)}
                    >
                      {savingCuentaId === cuenta.id ? 'Guardando...' : 'Guardar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2>Cheques pre-sistema (cartera)</h2>
        <p style={{ marginBottom: 12, color: '#555' }}>
          Registra cheques de terceros que ya estaban en cartera antes de usar el sistema. Crea un recibo y el cheque en estado &quot;recibido&quot;.
        </p>
        <form onSubmit={registrarCheque} className="informe-filters" style={{ alignItems: 'flex-end' }}>
          <div className="form-group">
            <label>Cliente</label>
            <select
              value={chequeForm.cliente_id}
              onChange={(e) => setChequeForm((p) => ({ ...p, cliente_id: e.target.value }))}
              required
            >
              <option value="">Seleccione...</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>{c.razon_social}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Banco del cheque</label>
            <select
              value={chequeForm.banco_id}
              onChange={(e) => setChequeForm((p) => ({ ...p, banco_id: e.target.value }))}
              required
            >
              <option value="">Seleccione...</option>
              {bancos.map((b) => (
                <option key={b.id} value={b.id}>{b.nombre}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>N° cheque</label>
            <input
              type="text"
              value={chequeForm.numero}
              onChange={(e) => setChequeForm((p) => ({ ...p, numero: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label>F. emisión</label>
            <input
              type="date"
              value={chequeForm.fecha_emision}
              onChange={(e) => setChequeForm((p) => ({ ...p, fecha_emision: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label>F. cobro</label>
            <input
              type="date"
              value={chequeForm.fecha_cobro}
              onChange={(e) => setChequeForm((p) => ({ ...p, fecha_cobro: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label>Importe</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={chequeForm.importe}
              onChange={(e) => setChequeForm((p) => ({ ...p, importe: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label>Fecha recibo</label>
            <input
              type="date"
              value={chequeForm.fecha}
              onChange={(e) => setChequeForm((p) => ({ ...p, fecha: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={chequeForm.es_electronico}
                onChange={(e) => setChequeForm((p) => ({ ...p, es_electronico: e.target.checked }))}
              />
              Electrónico
            </label>
          </div>
          <button type="submit" className="btn-primary" disabled={savingCheque}>
            {savingCheque ? 'Registrando...' : 'Registrar cheque'}
          </button>
        </form>
      </section>
      </>
      )}
    </div>
  );
};

export default PuestaEnMarcha;
